// Auth client — wired to Karthik's /api/auth/* endpoints.
//
// Session management:
//   The backend sets an HttpOnly cookie `cortex_session` on login/verify.
//   The frontend NEVER reads, stores, or sends a token manually — just
//   `credentials: 'include'` on every fetch.
//
// On page load, call `me()` to check if a session exists:
//   - 200 → user is logged in, returns PublicUser
//   - 401 → no session, show login page

import { type Lang, LangStore } from '@cortex/i18n';
import type { AttributionData } from './attribution';
import { reconcileAuthIdentity } from './clientCache';

const AUTH_BASE = '/api/auth';
const LISTENERS = new Set<(user: PublicUser | null) => void>();

let _cachedUser: PublicUser | null = null;

export type PublicUser = {
  sessionIdentity?: { name: string; email: string };
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  language: Lang;
  accountStatus: 'trial' | 'subscribed' | 'expired';
  trialPlan: 'basic' | 'starter' | 'growth' | null;
  trialEndsAt: number | null;
  creditsRemaining: number;
  monthlyCreditsAllowance: number;
  twoFactorEnabled: boolean;
  isAdmin: boolean;
  createdAt: number;
  lastLoginAt: number | null;
  phone: string | null;
  companyName: string | null;
  companyWebsite: string | null;
  timezone: string | null;
  uiPreferences: UiPreferences | null;
  deleteRequestedAt: number | null;
  deletedAt: number | null;
  /** Set by POST /api/auth/me/avatar; append `?v=${avatarUpdatedAt}` for cache busting. */
  avatarUrl: string | null;
  avatarUpdatedAt: number | null;
  /**
   * Raw attribution slice — currently just `source` (the `?source=` /
   * `?offer=` URL param or `ls_offer` cookie value persisted at signup).
   * Use the resolved `offer` field below for pricing/UX decisions; this
   * is exposed mainly for admin-style debugging.
   */
  attribution: { source: string | null };
  /**
   * Resolved offer config (LEA-230 / LEA-241). Backend resolves
   * `attribution.source` against the offer catalog on every `/me` read
   * and embeds the active config here, or null when no offer applies.
   * The frontend renders pricing-variant + slot overrides off this —
   * never trust attribution directly, always read this resolved value.
   */
  offer: PublicOffer | null;
  /**
   * PRISM-Rückkehrer (08.10.2026): true, wenn das Konto noch nie etwas gekauft hat (kein Abo,
   * kein Kauf, kein Umsatz, kein Guthaben). App.jsx zeigt damit einem abgelaufenen
   * PRISM-Konto wieder den Funnel statt des ExpiredGate. Fehlt bei älterer API.
   */
  neverBought?: boolean;
  /**
   * Reines Autopilot-Konto (21.10.2026). App.jsx nutzt es, bis /api/scraper/me geantwortet hat —
   * sonst blitzt bei Erstbesuchern das Pricing-Gate auf. Fehlt bei älterer API.
   */
  autopilotOnly?: boolean;
  /**
   * Nur-Lesen-Modus nach Ablauf: abgelaufenes Konto eines früheren Zahlers. App.jsx zeigt dann
   * den Hinweisbalken statt des ExpiredGate, schon bevor /api/scraper/me geantwortet hat.
   * Fehlt bei älterer API.
   */
  expiredViewOnly?: boolean;
};

/**
 * Public-facing slice of an OfferConfig from `@cortex/schemas`. Mirror of
 * the server-side type — kept here as a separate definition because the
 * prototype `.jsx` files read this off `window.__cortexUser` and we don't
 * want them to take a hard dependency on the schemas package.
 */
export type PublicOffer = {
  id: string;
  pricing: {
    variant: string;
    discountPercent?: number;
    stripeCouponId?: string;
    skip: boolean;
    /** LEA-453: SPA öffnet das Pricing-Modal beim 402 selbst (siehe App.jsx). */
    openOnZeroCredits?: boolean;
  };
  signup: {
    initialCredits: number | 'inherit';
    trialDaysOverride?: number;
    autoTrialPlan?: 'basic' | 'starter' | 'growth';
    /** LEA-453: Credits die beim Signup direkt ins Wallet gehen (direct2 = 50). */
    walletCredits?: number;
    /** LEA-453: false = keine lokale Trial-Uhr, das Wallet ist der Zähler. */
    localTrial?: boolean;
  };
  slots: Record<string, { kind: 'text'; textKey: string }>;
};

/**
 * LEA-453: Wird dieser 402 bereits zentral vom Pricing-Modal behandelt?
 *
 * Muss EXAKT die Öffnungsbedingung des Listeners in App.jsx spiegeln —
 * inklusive des Impersonation-Ausschlusses. Sonst entsteht ein stiller
 * Fehlschlag: der Listener öffnet nichts (weil impersoniert), die Call-Site
 * unterdrückt aber trotzdem ihren Toast, und der Klick gibt gar kein Feedback.
 *
 * Ein einziger Predicate für alle Call-Sites (Connect, Pitch, Run-Start), damit
 * die beiden Bedingungen nicht auseinanderlaufen können.
 */
export function isHandledByPricingModal(status?: number, errCode?: string): boolean {
  // 'plan_required' entsteht nur im Nur-Lesen-Modus nach Ablauf und nur, nachdem das
  // Pricing-Modal schon geöffnet wurde (lib/api.jsx) — der Toast wäre doppelt.
  if (status === 402 && errCode === 'plan_required') return true;
  if (status !== 402 || errCode !== 'insufficient_credits') return false;
  if (typeof window === 'undefined') return false;
  if (window.__cortexUser?.offer?.pricing?.openOnZeroCredits !== true) return false;
  try {
    // Spiegelt App.jsx: in einer Admin-Impersonation öffnet der Listener das
    // Modal bewusst NICHT (sein CTA würde einen echten Stripe-Checkout auf dem
    // fremden Account starten) — also darf der Toast auch nicht unterdrückt
    // werden.
    if (window.sessionStorage?.getItem('cortex_impersonate_uid')) return false;
  } catch {
    /* sessionStorage blockiert — dann eben nicht impersoniert */
  }
  return true;
}

/**
 * Nur-Lesen-Modus nach Ablauf (Janik 29.09.2026): frühere Zahler sehen und exportieren ihre
 * Leads weiter — die erste bezahlte Aktion (Suche, Kontakte, Akquiseplan, Firma anlegen,
 * Zeitplan) öffnet stattdessen das Pricing-Modal. App.jsx setzt `window.__cortexPlanRequired`
 * genau in diesem Modus und öffnet das Modal auf das Event.
 *
 * Call-Sites rufen das VOR ihrem optimistischen UI (Spinner, "gestartet"-Toast) auf und brechen
 * bei `true` ab. lib/api.jsx prüft dieselbe Bedingung als Backstop direkt vor dem Request.
 */
export function stopForPlan(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as unknown as { __cortexPlanRequired?: boolean }).__cortexPlanRequired !== true) {
    return false;
  }
  try {
    window.dispatchEvent(new CustomEvent('cortex:plan-required'));
  } catch {
    /* kein CustomEvent — Aktion bleibt trotzdem gestoppt */
  }
  return true;
}

export type UiPreferences = {
  leadsTableColumns?: string[];
  leadsTableColumnWidths?: Record<string, number>;
  onboardingDismissedAt?: number | null;
  adminUsersTableColumns?: string[];
  flags?: Record<string, unknown>;
};

export type EditableUserFields = {
  language?: Lang;
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  companyName?: string | null;
  companyWebsite?: string | null;
  timezone?: string | null;
  uiPreferences?: Partial<UiPreferences>;
};

export class AuthError extends Error {
  status: number;
  code: string | null;
  detail: string | null;
  issues: unknown[] | null;
  // Populated only by OAuth 409 responses ({ error: 'account_exists_other_provider',
  // provider: 'google' | 'microsoft' | 'password' }). Lets the LoginForm
  // render "please sign in with {provider}" without re-parsing the body.
  provider: 'google' | 'microsoft' | 'password' | null;
  /** Rate-limit wait time for the translated auth error. */
  retryAfterSec: number | null;

  constructor(status: number, body: Record<string, unknown>) {
    const msg =
      typeof body.detail === 'string'
        ? body.detail
        : typeof body.error === 'string'
          ? body.error
          : `Auth request failed (${status})`;
    super(msg);
    this.name = 'AuthError';
    this.status = status;
    this.code = typeof body.error === 'string' ? body.error : null;
    this.detail = typeof body.detail === 'string' ? body.detail : null;
    this.issues = Array.isArray(body.issues) ? body.issues : null;
    this.provider =
      body.provider === 'google' || body.provider === 'microsoft' || body.provider === 'password'
        ? body.provider
        : null;
    this.retryAfterSec =
      typeof body.retryAfterSec === 'number' && Number.isFinite(body.retryAfterSec)
        ? body.retryAfterSec
        : null;
  }
}

// LEA-403 follow-up: give every auth request its own client-side deadline. The
// server's authDeadline caps its OWN upstream calls at 6s, but that guard is a
// Promise.race+setTimeout — it cannot fire while the single Node event loop is
// itself blocked (in-process scraper/Prism-seed contention). In that regime the
// request stalls past the DO edge idle window and the browser sees a gateway
// 504. Without a client bound each retry then inherits the full ~60s edge
// window, so 3 attempts = ~3 minutes of spinner. A 20s per-attempt ceiling lets
// the CLIENT own its cadence: an attempt aborts fast and the next one starts.
const AUTH_FETCH_TIMEOUT_MS = 20_000;

// Ein Gateway-Fehler (504/502/503 mit HTML-Body) kommt NICHT von unserer API,
// sondern vom Edge davor. Ohne diese Zeile ist er in der Konsole von einem
// echten App-Fehler nicht zu unterscheiden — und seit der Boot-Screen sich
// selbst heilt (main.tsx bootReconnect) meldet ihn auch kein Kunde mehr.
// cf-ray + x-do-failure-code sind same-origin lesbar und sagen genau, WER den
// Fehler erzeugt hat: `UH / no_healthy_upstream` = die API-App war aus dem
// DigitalOcean-Upstream-Pool geflogen (gemessen auf PROD 2026-08-05).
function logGatewayFailure(status: number, res: Response, elapsedMs: number): void {
  const ray = res.headers.get('cf-ray');
  const doFailure = res.headers.get('x-do-failure-code');
  const doOrigStatus = res.headers.get('x-do-orig-status');
  console.warn(
    `[auth-gateway] ${status} on ${res.url} after ${elapsedMs}ms` +
      ` cf-ray=${ray ?? 'n/a'} do-failure=${doFailure ?? 'n/a'} do-orig-status=${doOrigStatus ?? 'n/a'}`,
  );
}

async function authFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const url = AUTH_BASE + path;
  const timeout = AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS);
  const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout;
  const startedAt = Date.now();
  const res = await fetch(url, {
    ...options,
    signal,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) ?? {}),
    },
  });
  if (!res.ok) {
    let body: Record<string, unknown> = {};
    let parsed = false;
    try {
      body = (await res.json()) as Record<string, unknown>;
      parsed = true;
    } catch {
      /* non-JSON response */
    }
    // Nicht-JSON + 5xx = Edge-Fehlerseite, nicht unsere API.
    if (!parsed && res.status >= 500) {
      logGatewayFailure(res.status, res, Date.now() - startedAt);
    }
    throw new AuthError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function notify(user: PublicUser | null): void {
  // LEA-298: wipe account-scoped client caches when the identity changes, so a
  // logout → new-signup on the same browser can't leak the prior account's
  // profile/lead data. No-op for a same-account reload (see clientCache.ts).
  reconcileAuthIdentity(user?.uid ?? null);
  _cachedUser = user;
  for (const cb of LISTENERS) cb(user);
}

// ─── Public API ──────────────────────────────────────────────────────────

export async function login(input: {
  email: string;
  password: string;
}): Promise<PublicUser> {
  const { user } = await withAuthRetry(() =>
    authFetch<{ user: PublicUser }>('/login', {
      method: 'POST',
      body: JSON.stringify({ email: input.email.trim(), password: input.password }),
    }),
  );
  notify(user);
  return user;
}

/**
 * Optional marketing-attribution payload forwarded with OAuth signups
 * (LEA-241): identical shape to the email/password `register()` body.
 * The backend persists this on first UserDoc create so `?source=` /
 * `ls_offer` cookies work for Google/Microsoft signups too.
 */
export type OauthAttribution = {
  ft?: string | null;
  structured?: AttributionData | null;
  source?: string | null;
};

function buildOauthBody(idToken: string, attribution?: OauthAttribution): string {
  // Current UI locale (e.g. from /es/register) so OAuth signups persist the
  // language the user is viewing — same as the email/password path. The
  // backend only consumes it when creating a brand-new UserDoc, so it's a
  // no-op for returning OAuth logins.
  const language = LangStore.get();
  // Only forward attribution when at least one signal is present —
  // matches the `register()` rule and keeps the OAuth wire contract
  // backwards-compatible for callers that don't care.
  const hasAttribution =
    !!attribution &&
    (!!attribution.ft ||
      !!attribution.source ||
      (attribution.structured && Object.keys(attribution.structured).length > 0));
  if (!hasAttribution) return JSON.stringify({ idToken, language });
  return JSON.stringify({
    idToken,
    language,
    attribution: {
      ft: attribution.ft ?? null,
      structured: (attribution.structured ?? {}) as Record<string, string>,
      source: attribution.source ?? null,
    },
  });
}

/**
 * Exchange a Firebase Google ID token for a cortex session cookie.
 *
 * Called by `firebase-auth-service.ts#authenticateWithGoogle` after the
 * Firebase popup resolves. Backend verifies the token via firebase-admin
 * `verifyIdToken`, then runs the same find-or-create + trial-grant +
 * Stripe-customer pipeline as `/signup`. New users get trial credits
 * automatically — no separate signup call needed.
 *
 * LEA-241: optional `attribution` is forwarded on the body so users
 * landing on `?source=50credits-trial` get the right offer when they
 * sign up via Google. Returning OAuth logins ignore the field — only
 * a brand-new UserDoc consumes it.
 *
 * On 409 account_exists_other_provider the AuthError carries the existing
 * provider in `body.provider` ('microsoft' | 'password'); LoginForm reads
 * it via `(err as AuthError).detail` is not enough — we surface it on
 * `code === 'account_exists_other_provider'` and the form looks at
 * `err.detail` (we copy provider into detail in mapAuthError) to render
 * the right "Bitte mit {provider} anmelden" toast.
 */
export async function loginWithGoogleIdToken(
  idToken: string,
  attribution?: OauthAttribution,
): Promise<PublicUser> {
  const { user } = await authFetch<{ user: PublicUser }>('/google', {
    method: 'POST',
    body: buildOauthBody(idToken, attribution),
  });
  notify(user);
  return user;
}

/**
 * Exchange a Firebase Microsoft ID token for a cortex session cookie.
 *
 * Symmetric with `loginWithGoogleIdToken` — V1.25.23 migrated Microsoft
 * sign-in onto Firebase Auth's `OAuthProvider('microsoft.com')`, so the
 * backend now runs the exact same `verifyIdToken` + `createSessionCookie`
 * pipeline as Google. New users get trial credits + a Stripe customer
 * in the same request.
 *
 * LEA-241: identical attribution-forwarding contract as the Google side.
 *
 * Errors mirror the Google side: 401 invalid_token, 409
 * account_exists_other_provider with `provider`, 503
 * firebase_not_configured.
 */
export async function loginWithMicrosoftIdToken(
  idToken: string,
  attribution?: OauthAttribution,
): Promise<PublicUser> {
  const { user } = await authFetch<{ user: PublicUser }>('/microsoft', {
    method: 'POST',
    body: buildOauthBody(idToken, attribution),
  });
  notify(user);
  return user;
}

export async function register(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  language?: Lang;
  attribution?: string | null;
  structured?: AttributionData | null;
  source?: string | null;
  /**
   * Cloudflare Turnstile token from the frontend widget. Optional —
   * backend has a symmetric kill-switch (TURNSTILE_ENABLED env var) so
   * an absent token is harmless when Turnstile is disabled (e.g. on
   * staging without env vars set). Forwarded as-is on the body.
   */
  turnstileToken?: string | null;
}): Promise<{ ok: true; uid: string }> {
  // Build the attribution payload only when we have at least one signal.
  // Sending an all-empty `{ ft: null, structured: {}, source: null }`
  // would still validate against the backend zod schema, but omitting it
  // entirely keeps the audit-log metadata noise-free for plain signups.
  const hasAttribution =
    !!input.attribution ||
    !!input.source ||
    (input.structured && Object.keys(input.structured).length > 0);

  return authFetch<{ ok: true; uid: string }>('/signup', {
    method: 'POST',
    body: JSON.stringify({
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      email: input.email.trim(),
      password: input.password,
      language: input.language ?? 'de',
      ...(hasAttribution
        ? {
            attribution: {
              ft: input.attribution ?? null,
              structured: (input.structured ?? {}) as Record<string, string>,
              source: input.source ?? null,
            },
          }
        : {}),
      ...(input.turnstileToken ? { turnstileToken: input.turnstileToken } : {}),
    }),
  });
}

export async function verifyEmail(token: string): Promise<PublicUser> {
  const { user } = await authFetch<{ user: PublicUser }>('/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  notify(user);
  return user;
}

export async function resendVerification(email?: string): Promise<void> {
  await authFetch('/verify-email/resend', {
    method: 'POST',
    body: JSON.stringify(email ? { email } : {}),
  });
}

export async function logout(): Promise<void> {
  try {
    await authFetch('/logout', { method: 'POST' });
  } catch {
    // Even if the server call fails, clear local state.
  }
  notify(null);
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Randomise each backoff ±25% so a fleet of tabs recovering from the SAME edge
// blip (or a just-restarted single instance) don't all retry in lockstep and
// hammer the origin the instant it comes back — a thundering herd that would
// re-stall the very event loop we're waiting on.
const jitter = (ms: number): number => Math.round(ms * (0.75 + Math.random() * 0.5));

// LEA-327: which non-401 failures are worth a quick retry on the idempotent
// /me read — 408 (request-timeout), 429 (rate-limit) and every 5xx, which
// crucially includes the intermittent gateway 502/503/504 the DO edge /
// Cloudflare proxy emit when requireAuth's verifySessionCookie + Firestore
// round-trip outruns the edge timeout. A bare network failure (fetch rejects
// with a TypeError, not an AuthError) is treated as transient too.
function isTransientAuthError(err: unknown): boolean {
  if (err instanceof AuthError) {
    return err.status === 408 || err.status === 429 || err.status >= 500;
  }
  // Non-AuthError here means fetch itself rejected (offline / DNS / reset) —
  // also worth one more attempt.
  return true;
}

// LEA-403: login + password-reset are the two auth paths a locked-out customer
// hits, and (unlike /me) they were never covered by the LEA-327 retry. They're
// POSTs but idempotent in effect — verifying a password / minting a fresh reset
// token — so a transient upstream failure is safe to retry. We retry ONLY on
// 5xx + bare network errors: the API now returns a fast, retryable 503
// (auth_upstream_timeout) where it used to hang to a gateway 504. A 401 (wrong
// credentials), 403 (disabled) or 429 (rate-limited) is a definitive answer and
// must surface immediately — never retried.
function isRetryableServerError(err: unknown): boolean {
  if (err instanceof AuthError) return err.status >= 500;
  return true;
}

async function withAuthRetry<T>(fn: () => Promise<T>, maxAttempts = 3): Promise<T> {
  // Widened past the original [300,900]: an in-process event-loop stall lasts
  // several seconds, so a 1.2s total budget was swallowed whole. Escalating +
  // jittered backoff spans a realistic stall/rollover window instead.
  const BACKOFF_MS = [400, 1200, 2500];
  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isRetryableServerError(err)) throw err;
      if (attempt < maxAttempts - 1) await sleep(jitter(BACKOFF_MS[attempt] ?? 2500));
    }
  }
  throw lastErr;
}

export async function me(): Promise<PublicUser | null> {
  // LEA-327: the session check is the FIRST request on every refresh and rides
  // requireAuth's verifySessionCookie(checkRevoked) + Firestore read. A single
  // transient gateway 504 used to hard-fail boot into the full-screen
  // "Connection error" banner (main.tsx bootError) with no auto-recovery —
  // that's the intermittent "Auth request failed (504)" on refresh. GET /me is
  // idempotent (no body, no mutation), so retry it a few times with short
  // backoff before giving up. A 401 still short-circuits straight to login.
  // Budget widened from 3×/1.2s: the recurring 504 is an in-process event-loop
  // stall (scraper/Prism-seed sharing this Node process), which lasts several
  // seconds — long enough to burn the old budget entirely and drop the user on
  // the full-screen banner. 5 attempts with jittered escalating backoff (each
  // itself capped by authFetch's 20s AbortSignal) outlast the stall, while a
  // genuinely dead origin still surfaces the banner in ~30s, not ~3 minutes.
  const MAX_ATTEMPTS = 5;
  const BACKOFF_MS = [400, 1000, 2200, 4000];
  let lastErr: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const { user, operatorSession } = await authFetch<{
        user: PublicUser;
        operatorSession?: { kind?: string; name?: string; by: string } | null;
      }>('/me');
      if (operatorSession?.kind === 'customer') {
        user.sessionIdentity = { name: operatorSession.name || operatorSession.by, email: operatorSession.by };
      }
      notify(user);
      return user;
    } catch (err) {
      if (err instanceof AuthError && err.status === 401) {
        notify(null);
        return null;
      }
      lastErr = err;
      if (!isTransientAuthError(err)) throw err;
      if (attempt < MAX_ATTEMPTS - 1) await sleep(jitter(BACKOFF_MS[attempt] ?? 4000));
    }
  }
  // Retries exhausted — surface the last transient error so bootApp shows the
  // connection-error banner (a genuine, sustained outage rather than a blip).
  throw lastErr;
}

export async function updateProfile(fields: EditableUserFields): Promise<PublicUser> {
  const { user } = await authFetch<{ user: PublicUser }>('/me', {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
  notify(user);
  return user;
}

export async function resetPasswordRequest(email: string): Promise<void> {
  // LEA-403: reset is far less latency-sensitive than login and its per-IP
  // budget is consumed per-attempt, so cap retries at 2 (vs login's default 3).
  await withAuthRetry(
    () =>
      authFetch('/reset-password-request', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      }),
    2,
  );
}

export async function resetPasswordConfirm(
  token: string,
  newPassword: string,
  // LEA-527: onboarding = first-time set-password from the branded pre-provision link.
  // Tells the backend to skip the "password reset" security-alert email; the response
  // then carries the account email so the caller can auto-login the customer.
  opts?: { onboarding?: boolean },
): Promise<{ email?: string }> {
  // Backend's resetConfirmSchema expects `password`, not `newPassword`
  // (see apps/api/src/routes/auth.ts → resetConfirmSchema). The local
  // arg name stays `newPassword` for callsite clarity at the UI layer.
  return authFetch<{ email?: string }>('/reset-password-confirm', {
    method: 'POST',
    body: JSON.stringify({
      token,
      password: newPassword,
      ...(opts?.onboarding ? { mode: 'onboarding' } : {}),
    }),
  });
}

// LEA-527: peek a pre-provision set-password token (no consume) so the branded page
// can fail fast on an expired link and greet the customer by name.
export async function setPasswordInfo(
  token: string,
): Promise<{ email: string; firstName: string | null }> {
  return authFetch<{ email: string; firstName: string | null }>(
    `/set-password/info?token=${encodeURIComponent(token)}`,
  );
}

// LEA-527: self-service "send me a new link" from the expired set-password page. Mints a fresh
// 14-day link and emails it to the account address (no support round-trip). `alreadySet: true`
// means the password is already set — the page should route the customer to normal login.
export async function resendSetPassword(
  token: string,
): Promise<{ ok: boolean; alreadySet?: boolean }> {
  return authFetch<{ ok: boolean; alreadySet?: boolean }>('/set-password/resend', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await authFetch('/password-change', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function deleteAccountRequest(
  currentPassword: string,
): Promise<{ deletedAt: number }> {
  return authFetch<{ deletedAt: number }>('/account/delete-request', {
    method: 'POST',
    body: JSON.stringify({ currentPassword }),
  });
}

export async function deleteAccountCancel(email: string, password: string): Promise<void> {
  await authFetch('/account/delete-cancel', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function exportData(): Promise<Blob> {
  const res = await fetch(AUTH_BASE + '/account/export', {
    credentials: 'include',
  });
  if (!res.ok) throw new AuthError(res.status, { error: 'export_failed' });
  return res.blob();
}

// ─── Observers + cached access ───────────────────────────────────────────

export function onAuthChange(cb: (user: PublicUser | null) => void): () => void {
  LISTENERS.add(cb);
  return () => LISTENERS.delete(cb);
}

export function getCachedUser(): PublicUser | null {
  return _cachedUser;
}
